'use client';
import React, { useState, useEffect } from 'react';
import { Pencil, PlusCircle, Trash2, X } from 'lucide-react';
export default function PollsManager() {
  const [polls, setPolls] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    published: false,
    showOnHome: false,
    allowGuests: false,
    startDate: '',
    endDate: '',
    limitedStores: '',
    systemKeyword: '',
    displayOrder: 0,
  });
  // Load saved polls
  useEffect(() => {
    const saved = localStorage.getItem('polls');
    if (saved) setPolls(JSON.parse(saved));
  }, []);
  // Save polls to localStorage
  useEffect(() => {
    localStorage.setItem('polls', JSON.stringify(polls));
  }, [polls]);
  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('Please enter a poll name.');
      return;
    }
    if (formData.id) {
      setPolls((prev) =>
        prev.map((p) => (p.id === formData.id ? formData : p))
      );
    } else {
      setPolls((prev) => [...prev, { ...formData, id: Date.now() }]);
    }
    setFormData({
      id: null,
      name: '',
      published: false,
      showOnHome: false,
      allowGuests: false,
      startDate: '',
      endDate: '',
      limitedStores: '',
      systemKeyword: '',
      displayOrder: 0,
    });
    setShowForm(false);
  };
  const handleEdit = (poll) => {
    setFormData(poll);
    setShowForm(true);
  };
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this poll?')) {
      setPolls(polls.filter((p) => p.id !== id));
    }
  };
  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-semibold text-gray-800">Polls</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          <PlusCircle size={18} /> Add New
        </button>
      </div>
      {/* Table */}
      <div className="bg-white shadow-md rounded-xl overflow-x-auto">
        <table className="min-w-full text-sm text-left text-gray-700">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Published</th>
              <th className="px-4 py-3">Show on Home</th>
              <th className="px-4 py-3">Guests Vote</th>
              <th className="px-4 py-3">Start Date</th>
              <th className="px-4 py-3">End Date</th>
              <th className="px-4 py-3">Display Order</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {polls.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-400">
                  No polls available.
                </td>
              </tr>
            ) : (
              polls.map((poll) => (
                <tr key={poll.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3">{poll.name}</td>
                  <td className="px-4 py-3 text-center">
                    {poll.published ? '✓' : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {poll.showOnHome ? '✓' : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {poll.allowGuests ? '✓' : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {poll.startDate ? poll.startDate : '-'}
                  </td>
                  <td className="px-4 py-3">
                    {poll.endDate ? poll.endDate : '-'}
                  </td>
                  <td className="px-4 py-3">{poll.displayOrder}</td>
                  <td className="px-4 py-3 flex gap-3 justify-center">
                    <button
                      onClick={() => handleEdit(poll)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Pencil size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(poll.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Full Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 overflow-auto">
          <div className="bg-white w-[95%] md:w-[85%] lg:w-[70%] xl:w-[60%] rounded-lg shadow-xl my-6">
            {/* Header */}
            <div className="flex justify-between items-center border-b px-6 py-4 bg-gray-100">
              <h2 className="text-lg font-semibold text-gray-700">
                {formData.id ? 'Edit Poll' : 'Add a New Poll'}
              </h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            {/* Form */}
            <div className="p-6">
              <h3 className="text-md font-medium text-gray-700 mb-4">
                Poll Info
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Name */}
                <div className="flex flex-col">
                  <label className="text-sm text-gray-600 mb-1">Name *</label>
                  <input
                    type="text"
                    className="border p-2 rounded-md"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Enter poll name"
                  />
                </div>
                {/* Published */}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) =>
                      setFormData({ ...formData, published: e.target.checked })
                    }
                  />
                  Published
                </label>
                {/* Show on Home */}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.showOnHome}
                    onChange={(e) =>
                      setFormData({ ...formData, showOnHome: e.target.checked })
                    }
                  />
                  Show on home page
                </label>
                {/* Allow guests */}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.allowGuests}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        allowGuests: e.target.checked,
                      })
                    }
                  />
                  Allow guests to vote
                </label>
                {/* Dates */}
                <div>
                  <label className="text-sm text-gray-600 mb-1">
                    Start Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={(e) =>
                      setFormData({ ...formData, startDate: e.target.value })
                    }
                    className="border p-2 rounded-md w-full"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1">End Date</label>
                  <input
                    type="datetime-local"
                    value={formData.endDate}
                    onChange={(e) =>
                      setFormData({ ...formData, endDate: e.target.value })
                    }
                    className="border p-2 rounded-md w-full"
                  />
                </div>
                {/* Limited to Stores */}
                <div>
                  <label className="text-sm text-gray-600 mb-1">
                    Limited to stores
                  </label>
                  <input
                    type="text"
                    value={formData.limitedStores}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        limitedStores: e.target.value,
                      })
                    }
                    className="border p-2 rounded-md w-full"
                    placeholder="Store names"
                  />
                </div>
                {/* System Keyword */}
                <div>
                  <label className="text-sm text-gray-600 mb-1">
                    System keyword
                  </label>
                  <input
                    type="text"
                    value={formData.systemKeyword}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        systemKeyword: e.target.value,
                      })
                    }
                    className="border p-2 rounded-md w-full"
                    placeholder="Optional keyword"
                  />
                </div>
                {/* Display Order */}
                <div>
                  <label className="text-sm text-gray-600 mb-1">
                    Display order
                  </label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        displayOrder: e.target.value,
                      })
                    }
                    className="border p-2 rounded-md w-full"
                  />
                </div>
              </div>
              {/* Poll Answers */}
              <div className="mt-8 border-t pt-4">
                <h3 className="text-md font-medium text-gray-700 mb-2">
                  Poll answers
                </h3>
                <p className="text-sm text-gray-500">
                  You need to save the poll before you can add answers for this
                  poll page.
                </p>
              </div>
              {/* Buttons */}
              <div className="flex justify-end mt-6 gap-3">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
